import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Check } from "lucide-react";
import { BriefSubscribe } from "@/components/BriefSubscribe";
import { PublicShell } from "@/components/layout/PublicShell";
import { SEO } from "@/lib/seo";
import { ListingSkeletons } from "@/components/ListingCard";
import { ButtonLink, EmptyState, Notice, Panel, SelectField, Status } from "@/components/system";
import { useCity } from "@/contexts/CityContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  ALERT_CATEGORY_LABELS,
  ALERT_SEVERITY_LABELS,
  formatRelativeTime,
  useAlerts,
  useMarkAlertRead,
  type AlertCategory,
  type AlertSeverity,
} from "@/lib/alerts";
import {
  ALERT_LEVELS,
  DEFAULT_CURRENT_WINDOW_DAYS,
  alertLevelFromSeverity,
  alertSourceLinkLabel,
  alertTone,
  formatAlertDate,
  formatRelativeAlertTime,
  isCurrentAlert,
  usePublicAlerts,
  type AlertLevel,
  type PublicAlert,
} from "@/lib/public-alerts";

/**
 * Pillar 1 landing: Public Health + Regional News Feed.
 *
 * This is the first URL a GP opens in the morning. Design priorities:
 *   1. Unread state is visually obvious at a glance (ring/weight, not a dot).
 *   2. Severity colour-coding is conservative — only urgent/critical use
 *      the warm accent; everything else uses muted palette so the page
 *      doesn't look like a Christmas tree.
 *   3. Clicking a card marks read optimistically + navigates to detail.
 *      Reading the detail is the implicit "I've seen this" signal.
 */

const CATEGORY_OPTIONS: Array<{ value: AlertCategory | "all"; label: string }> = [
  { value: "all", label: "All categories" },
  { value: "public_health", label: "Public health" },
  { value: "infectious_disease", label: "Infectious disease" },
  { value: "immunisation", label: "Immunisation" },
  { value: "medication", label: "Medication" },
  { value: "recall", label: "Recall" },
  { value: "clinical_guideline", label: "Clinical guideline" },
  { value: "workforce", label: "Workforce" },
  { value: "other", label: "Other" },
];

const SEVERITY_OPTIONS: Array<{ value: AlertSeverity | "all"; label: string }> = [
  { value: "all", label: "All levels" },
  { value: "critical", label: "Urgent" },
  { value: "warning", label: "Warning" },
  { value: "advisory", label: "Advisory" },
  { value: "info", label: "Update" },
];

/** Feeds older than this are flagged so a quiet page isn't mistaken for an all-clear. */
const STALE_FEED_MS = 6 * 60 * 60_000;

export default function Alerts() {
  const { city, link } = useCity();
  const { isAuthenticated } = useAuth();
  const [category, setCategory] = useState<AlertCategory | "all">("public_health");
  const [severity, setSeverity] = useState<AlertSeverity | "all">("all");
  const [unreadOnly, setUnreadOnly] = useState(false);

  const filters = {
    category: category === "all" ? undefined : category,
    severity: severity === "all" ? undefined : severity,
    unreadOnly: unreadOnly || undefined,
    enabled: isAuthenticated,
  };

  const { data, isLoading, isError, error } = useAlerts(city, filters);
  const markRead = useMarkAlertRead(city);

  if (!isAuthenticated) {
    return <PublicAlertsPreview citySlug={city.slug} cityName={city.name} />;
  }

  const items = data?.items ?? [];
  const unreadCount = items.filter((a) => !a.read_at).length;

  return (
    <PublicShell>
      <SEO
        title={`Alerts · ${city.name} · PulseBoard`}
        description={`Public health alerts curated for GPs in ${city.name}.`}
        path="/alerts"
        noindex
      />
      <div className="ds-container">
        <header className="ds-page-head">
          <p className="ds-eyebrow ds-eyebrow--teal">Public health · {city.name}</p>
          <h1 className="ds-display-1">Public health alerts</h1>
          <p className="ds-lead">Verified ACT Government clinician alerts and national clinical safety notices for {city.name}.</p>
          {items.length > 0 ? (
            <div className="ds-facts">
              <span><b className="ds-num">{unreadCount}</b> unread of {items.length} showing</span>
            </div>
          ) : null}
        </header>

        <div className="ds-panel ds-panel--tight ds-toolbar" aria-label="Alert filters">
          <div className="ds-toolbar__filters">
            <SelectField label="Category" hideLabel value={category} onChange={(event) => setCategory(event.target.value as AlertCategory | "all")}>
              {CATEGORY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </SelectField>
            <SelectField label="Severity" hideLabel value={severity} onChange={(event) => setSeverity(event.target.value as AlertSeverity | "all")}>
              {SEVERITY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </SelectField>
            <button type="button" className="ds-chip" aria-pressed={unreadOnly} onClick={() => setUnreadOnly((v) => !v)}>
              Unread only
            </button>
            <span className="ds-toolbar__count">
              <ButtonLink to={link("/alerts/subscriptions")} variant="ghost" size="sm">Alert settings</ButtonLink>
            </span>
          </div>
        </div>

        <section className="ds-section" aria-label="Alerts">
          {isLoading ? (
            <ListingSkeletons count={3} />
          ) : isError ? (
            <EmptyState tone="error" icon={<AlertCircle />} title="Alerts didn't load">
              {error instanceof Error ? error.message : "Check your connection and try again."}
            </EmptyState>
          ) : items.length === 0 ? (
            <EmptyState icon={<Check />} title="You're all caught up">
              No alerts match your filters in {city.name} right now.
            </EmptyState>
          ) : (
            <ol className="ds-stack" style={{ gap: 12, listStyle: "none", margin: 0, padding: 0 }}>
              {items.map((alert) => {
                const isUnread = !alert.read_at;
                return (
                  <li key={alert.id}>
                    <Link
                      to={link(`/alerts/${alert.id}`)}
                      onClick={() => {
                        if (isUnread) markRead.mutate(alert.id);
                      }}
                      className={`ds-card ds-alert${isUnread ? " ds-alert--unread" : ""}`}
                    >
                      <AlertMeta
                        level={alertLevelFromSeverity(alert.severity)}
                        severityLabel={ALERT_SEVERITY_LABELS[alert.severity]}
                        source={ALERT_CATEGORY_LABELS[alert.category]}
                        time={alert.published_at}
                        timeLabel={formatRelativeTime(alert.published_at)}
                        isNew={isUnread}
                      />
                      <h2 className="ds-title">{alert.title}</h2>
                      {alert.summary ? <p className="ds-small ds-clamp-2">{alert.summary}</p> : null}
                      {alert.tags && alert.tags.length > 0 ? (
                        <ul className="ds-listing__tags">
                          {alert.tags.slice(0, 4).map((tag) => <li key={tag} className="ds-tag">{tag}</li>)}
                        </ul>
                      ) : null}
                    </Link>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      </div>
    </PublicShell>
  );
}

function PublicAlertsPreview({
  citySlug,
  cityName,
}: {
  citySlug: string;
  cityName: string;
}) {
  const { data, isLoading, isError } = usePublicAlerts(citySlug, 10, {
    category: "public_health",
  });
  const items = data?.items ?? [];
  const windowDays = data?.meta.currentWindowDays ?? DEFAULT_CURRENT_WINDOW_DAYS;
  const current = items.filter((alert) => isCurrentAlert(alert, windowDays));
  const earlier = items.filter((alert) => !isCurrentAlert(alert, windowDays));
  const latest = items[0] ?? null;
  const checkedAt = data?.meta.lastSuccessAt ?? null;
  const feedIsStale = checkedAt ? Date.now() - new Date(checkedAt).getTime() > STALE_FEED_MS : false;

  return (
    <PublicShell>
      <SEO
        title={`Public health alerts · ${cityName} · PulseBoard`}
        description={`Public ACT health alerts and clinician-source monitoring for ${cityName}.`}
        path="/alerts"
      />
      <div className="ds-container">
        <header className="ds-page-head">
          <p className="ds-eyebrow ds-eyebrow--teal">Public health · {cityName}</p>
          <h1 className="ds-display-1">Public health alerts</h1>
          <p className="ds-lead">Notices from ACT Health and national health sources, graded so the ones that need attention stand out.</p>
          {checkedAt ? (
            <div className="ds-facts">
              <span>Official feeds checked <b>{formatRelativeAlertTime(checkedAt)}</b></span>
              <span>Current means the last <b>{windowDays}</b> days</span>
            </div>
          ) : null}
        </header>

        <div className="ds-split">
          <div className="ds-stack" style={{ gap: 32 }}>
            {isLoading ? (
              <ListingSkeletons count={3} />
            ) : isError ? (
              <EmptyState tone="error" icon={<AlertCircle />} title="Alerts didn't load">
                Check your connection and try again. Official notices are also published at act.gov.au/health.
              </EmptyState>
            ) : (
              <>
                <section className="ds-stack" style={{ gap: 12 }} aria-labelledby="alerts-current">
                  <h2 id="alerts-current" className="ds-title">Current</h2>
                  {current.length > 0 ? (
                    <AlertList alerts={current} />
                  ) : (
                    <Notice>
                      <p>
                        <b>Nothing new in the last {windowDays} days.</b>{" "}
                        {latest
                          ? `The latest notice was "${latest.title}" from ${latest.source_name ?? "an official source"} on ${formatAlertDate(latest.published_at)}.`
                          : `No public health notices have been published for ${cityName} yet.`}
                      </p>
                      {feedIsStale && checkedAt ? (
                        <p className="ds-small">The feeds were last checked on {formatAlertDate(checkedAt)}, so a newer notice may not be here yet.</p>
                      ) : null}
                    </Notice>
                  )}
                </section>

                {earlier.length > 0 ? (
                  <section className="ds-stack" style={{ gap: 12 }} aria-labelledby="alerts-earlier">
                    <h2 id="alerts-earlier" className="ds-title">Earlier notices</h2>
                    <AlertList alerts={earlier} />
                  </section>
                ) : null}
              </>
            )}
          </div>

          <div className="ds-stack" style={{ gap: 16 }}>
            <Panel>
              <div className="ds-stack" style={{ gap: 12 }}>
                <h2 className="ds-title">How notices are graded</h2>
                <p className="ds-small">
                  ACT Health doesn&apos;t grade its alerts. PulseBoard grades each one from its own wording, using the rules below. Always read the official notice.
                </p>
                <dl className="ds-stack" style={{ gap: 10, margin: 0 }}>
                  {([4, 3, 2, 1] as AlertLevel[]).map((level) => (
                    <div key={level} className="ds-cluster" style={{ gap: 10, alignItems: "baseline", flexWrap: "nowrap" }}>
                      <dt style={{ minWidth: 92 }}><Status tone={alertTone(level)}>{ALERT_LEVELS[level].label}</Status></dt>
                      <dd className="ds-small" style={{ margin: 0 }}>{ALERT_LEVELS[level].meaning}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </Panel>
            <BriefSubscribe sourcePath="/alerts" />
          </div>
        </div>
      </div>
    </PublicShell>
  );
}

function AlertList({ alerts }: { alerts: PublicAlert[] }) {
  return (
    <ol className="ds-stack" style={{ gap: 12, listStyle: "none", margin: 0, padding: 0 }}>
      {alerts.map((alert) => (
        <li key={alert.id}>
          {/* Never render <a> without href: alerts without a source URL are plain articles. */}
          <article className="ds-card ds-alert">
            <AlertMeta
              level={alert.level}
              severityLabel={ALERT_LEVELS[alert.level].label}
              source={alert.source_name ?? "Official health source"}
              time={alert.published_at}
              timeLabel={formatAlertDate(alert.published_at)}
            />
            <h3 className="ds-title">{alert.title}</h3>
            {alert.summary ? <p className="ds-small">{alert.summary}</p> : null}
            {alert.source_url ? (
              <a className="ds-link ds-small" href={alert.source_url} target="_blank" rel="noopener">
                {alertSourceLinkLabel(alert)}
              </a>
            ) : null}
          </article>
        </li>
      ))}
    </ol>
  );
}

function AlertMeta({
  level,
  severityLabel,
  source,
  time,
  timeLabel,
  isNew = false,
}: {
  level: AlertLevel;
  severityLabel: string;
  source: string;
  time: string;
  timeLabel: string;
  isNew?: boolean;
}) {
  const tone = alertTone(level);
  return (
    <div className="ds-listing__top">
      <span className="ds-cluster" style={{ gap: 10 }}>
        <Status tone={tone}>{severityLabel}</Status>
        <span className="ds-eyebrow">{source}</span>
        {isNew ? <span className="ds-eyebrow ds-eyebrow--teal">New</span> : null}
      </span>
      <time className="ds-fine" dateTime={time} style={{ whiteSpace: "nowrap" }}>{timeLabel}</time>
    </div>
  );
}
